import type { Choice } from "../hooks/PredictionMarket/usePredictionMarket";
import type { PredictionMarketType } from "../utils/predictionMarket/utils";
import type { WebUser } from "./WebUser";

export type PredictionMarket = {
  id: string;
  title: string;
  description: string;
  commentCount: number;
  primaryImageURL: string | null;
  status: PredictionMarketStatus;
  predictionMarketChoices: PredictionMarketChoice[];
  entryOpenAt: number; // unix milliseconds
  entryCloseAt: number; // unix milliseconds
  realEndTime: number | null; // unix milliseconds
  liquidityParameter: number;
  label: string;
  subLabel: string;
};

export type PredictionMarketChoice = {
  id: string;
  name: string;
};

export type PredictionMarketChoiceWithProbability = PredictionMarketChoice & {
  probability: number;
  commentCount?: number;
};

export type PredictionMarketWithProbability = Omit<PredictionMarket, "predictionMarketChoices"> & {
  predictionMarketChoices: PredictionMarketChoiceWithProbability[];
};

export type PredictionMarketMyEntry = {
  id: string;
  predictionMarketId: string;
  primaryImageURL?: string | null;
  label: string;
  subLabel: string;
  type: PredictionMarketType;
  choices: Choice[];
  title: string;
  freePoints: string;
  freePointsPool: string;
  potentialFreePoints: string;
  entryCloseAt: number;
  entryAt: number;
  myChoice: PredictionMarketChoiceWithProbability;
  hasAppLovinToken?: boolean;
};

export enum PredictionMarketStatus {
  Open = "Open",
  PrizeDistributed = "PrizeDistributed",
  Closed = "Closed",
}

export type PredictionMarketHistoryChoiceSnapshot = {
  choiceId: string;
  choiceName: string;
  probability: number;
  totalScore: string;
  totalFreePoints: string;
  commentCount: number;
};

export type PredictionMarketSnapshot = {
  timeStamp: number;
  predictionChoiceStates: PredictionMarketHistoryChoiceSnapshot[];
};

export type PredictionMarketHistory = {
  timestamps: number[];
  [choiceId: PredictionMarketChoice["id"]]: number[];
};

type MarketRankBase = {
  userId: string;
  userName: string;
  userPhotoUrl: string | null;
  rank: number;
  freePointsEntered: number;
};

type MyRank = {
  predictionMarketChoiceName: string;
  rank: number;
  freePointsEntered: number;
  freePointsToNextRank: number | null;
  potentialPrize: number;
};

export type MarketRankLiveRank = MarketRankBase & {
  score: number;
  potentialPrize: number;
};

export type MarketRankLive = {
  predictionMarketChoiceId: string;
  predictionMarketChoiceName: string;
  ranks: MarketRankLiveRank[];
};
export type MarketRankEnded = {
  ranks: (MarketRankBase & { prizes: number })[];
};

export type PredictionMarketRanking =
  | { status: "live"; marketRanks: MarketRankLive[] }
  | {
      status: "ended";
      winningChoice: PredictionMarketChoice;
      marketRanks: MarketRankEnded;
    };

export type MyPredictionMarketRanking =
  | {
      status: "live";
      marketRanks: MarketRankLive[];
      myRanks: MyRank[];
    }
  | {
      status: "ended";
      winningChoice: PredictionMarketChoice;
      marketRanks: MarketRankEnded;
      totalUserPrize: number;
    };

type PredictionMarketMyResultBase = {
  id: string;
  predictionMarketId: string;
  primaryImageURL?: string | null;
  freePoints: string;
  freePointsPool: string;
  potentialPrize: string;
  prizeType: "FreePoints" | "RedeemablePoints" | "Unclaimed";
  label: string;
  title: string;
  subLabel: string;
  type: PredictionMarketType;
  choices: Choice[];
  entryCloseAt: number;
  myChoice: PredictionMarketChoiceWithProbability;
};

export type PredictionMarketMyResult =
  | (PredictionMarketMyResultBase & {
      result: "win";
      prize: string;
    })
  | (PredictionMarketMyResultBase & {
      result: "loss";
      participantPrize: string;
    });

export type WinningChoice = PredictionMarketChoiceWithProbability & {
  freePoints: string;
  prize: string;
};

export type UserWithPrize = {
  id: WebUser["id"];
  name: WebUser["name"];
  photoUrl: WebUser["photoUrl"];
  freePoints: string;
  prize: string;
};

export type PredictionMarketRecentResult = {
  id: string;
  freePointsPool: string;
  label: string;
  title: string;
  entryCloseAt: number;
  winningChoice: WinningChoice;
  top3Users: UserWithPrize[];
};
